// Features: ML001-F0117, ML001-F0119, ML001-F0122, ML001-F0123, M004-F0709, M004-F0710, M004-F0711
// Tests: T-LED-NODEUI-011, T-LED-NODEUI-013, T-LED-WEB-006, T-M004-0197, T-M004-0198, T-M004-0200
//
// ============================================================================
// PROVISIONAL WIRE ADAPTER. This is the ONE place the three-queue wire shape lives.
// ============================================================================
// The customer ledger page's "Advice" and "Approval — next invoice" sections
// (Ledger #62) read and write ONLY through Core's account gateway
// (`ledger.account.request`). Core #268 adds the gateway operations and
// Spec #37 (Spec project 29) owns their interface rows.
//
// Shapes: Spec core-ledger-account-gateway/v1 1.1.0, digest cfd1a890…, as
// MERGED by Spec !72 (merge e528aa4f5eccc3e184d1fafb880cebd6022f3c9d), re-pinned
// from 1.0.0 (Spec !71, merge 1987973171f62e9317a6a30c6e6c095374620035) in the
// 2026-10-01 WebUI integration batch. 1.1.0 adds declared refusal (status, code)
// pairs — 422 LEDGER_VAT_REQUIRED (add_manual, draft.approve), 404
// LEDGER_ADVICE_NOT_FOUND / LEDGER_DRAFT_LINE_NOT_FOUND, 409
// LEDGER_ADVICE_NOT_READY — surfaced by LedgerQueueSections' KNOWN_REFUSALS and
// its generic fallback; this adapter's wire shape is unchanged by the re-pin.
// 2026-10-04 (ML001-F0122, ML001-F0123): re-pinned to 1.2.0 at the head of Spec !73 (commit
// 82df595e44b769c094fc5b2ccb0a89179eb61201, an UNMERGED DRAFT: re-pin to its merge commit). 1.2.0 is additive:
//   AdviceItem gains optional hand_off_id, notice_state, session_ended;
//   releaseLedgerAdvice        (advice.release) POST …/advice/release  {hand_off_id, reason};
//                                Core-terminated, SAL 4, Idempotency-Key required
//   getLedgerInvoiceLineHistory (invoice.line.history) GET …/invoice-lines/{handOffId}/history
//                                ledger_manage, SAL 3; 404 when the item has no line
// Pinned in tests/ledger-account-gateway/contract-pin.json. The contract paths
// are /api/v1/orgs/{orgId}/ledger/users/{userId}/…; the api client's base URL
// already carries /api, as it does for credit.grant.
//   listLedgerAdvice     (advice.list)      GET  …/advice
//   getLedgerDraft       (draft.get)        GET  …/draft
//   editLedgerDraftLine  (draft.line.edit)  POST …/draft/lines    oneOf by action; Idempotency-Key required
//   approveLedgerDraft   (draft.approve)    POST …/draft/approve  {invoice_id, expected_total_minor}; Idempotency-Key
// Every request this module builds and every response the tests feed it is
// validated by Spec's generated client (tests/ledger-account-gateway/conformance.mjs).
// A re-pin is a change to this file and the pin only: the page consumes
// normaliseAdvice()/normaliseDraft()/normaliseIssued() and the four client functions.
// ============================================================================

import { displayTxId, txIdFromHandOffId, wellFormedTxId, wellFormedTxnId } from '../utils/transactionId';

const base = (orgId, userId) => `/v1/orgs/${encodeURIComponent(orgId)}/ledger/users/${encodeURIComponent(userId)}`;

// Capability gate: the contract's declared answer for an operation the linked node
// does not offer (ErrorReadInvalid, 400 LEDGER_ACCOUNT_OPERATION_UNSUPPORTED).
// A 404 carrying one of the contract's OWN declared codes (ErrorNotFound's
// enum: LEDGER_NODE_NOT_LINKED, TUNNEL_NOT_CONNECTED, LEDGER_INVOICE_NOT_FOUND)
// is NOT treated as unsupported: it is shown, never hidden.
export const UNSUPPORTED_CODES = Object.freeze([
  'LEDGER_ACCOUNT_OPERATION_UNSUPPORTED',
]);

// Core #268 / fe4651c8's gateway routes are not yet on prod Core (4a96a0da):
// an unrecognised route answers Core's OWN generic routing-layer refusal,
// `404 {"error":"NOT_FOUND"}` — never a code the contract declares (it is not
// in ErrorNotFound's enum above, nor in any other declared enum; a real
// linked node that simply can't route the call would say
// LEDGER_NODE_NOT_LINKED / TUNNEL_NOT_CONNECTED instead). On the two READS
// only (advice.list, draft.get), this closes the capability gate exactly like
// LEDGER_ACCOUNT_OPERATION_UNSUPPORTED, so the page stays byte-identical to
// the baseline instead of showing an error banner for a route that simply
// does not exist yet. Writes are unaffected: this predicate is never
// consulted on the write path, so a declared contract 404 on a write
// (LEDGER_ADVICE_NOT_FOUND, LEDGER_DRAFT_LINE_NOT_FOUND, ...) still shows.
export const isRouteNotFoundError = (error) => Number(error?.response?.status) === 404 && errorCode(error) === 'NOT_FOUND';

export const LEDGER_QUEUE_OPERATIONS = Object.freeze({
  listAdvice: { op: 'advice.list', method: 'GET', path: '/advice' },
  getDraft: { op: 'draft.get', method: 'GET', path: '/draft' },
  editDraft: { op: 'draft.line.edit', method: 'POST', path: '/draft/lines' },
  approveDraft: { op: 'draft.approve', method: 'POST', path: '/draft/approve' },
  // M004-F0723 (previewLedgerDraft): read-only, ledger_manage, no SAL, no Idempotency-Key.
  previewDraft: { op: 'draft.preview', method: 'GET', path: '/draft/preview' },
  // Spec 1.2.0. releaseAdvice terminates in Core (no Ledger frame).
  releaseAdvice: { op: 'advice.release', method: 'POST', path: '/advice/release' },
  getInvoiceLineHistory: { op: 'invoice.line.history', method: 'GET', path: '/invoice-lines/{handOffId}/history' },
});

// advice.release refusals (Spec 1.2.0): 404 ADVICE_RELEASE_NOT_FOUND, 409
// ADVICE_RELEASE_NOT_ELIGIBLE with a top-level `reason` (not_ended | not_held |
// already_released) and 409 ADVICE_RELEASE_IDEMPOTENCY_CONFLICT.
export const RELEASE_NOT_FOUND = 'ADVICE_RELEASE_NOT_FOUND';
export const RELEASE_NOT_ELIGIBLE = 'ADVICE_RELEASE_NOT_ELIGIBLE';
export const RELEASE_IDEMPOTENCY_CONFLICT = 'ADVICE_RELEASE_IDEMPOTENCY_CONFLICT';
// Core #298 / M001-D022-F064 (M004-F0710): 409 LEDGER_FEED_NOT_BOUND. Core's body is {success:false, error, message,
// details:{tx_id, hand_off_id, observed_at, outbox_id}}; `message` already ends in " (TX-xxxxxxxx)". NOT YET DECLARED by Spec
// (the 409 bodies are ReleaseConflict): recorded as a Spec need, handled here by code.
export const FEED_NOT_BOUND = 'LEDGER_FEED_NOT_BOUND';
const FEED_NOT_BOUND_FALLBACK = "The Ledger has not bound this organisation's occurrence feed, so this item cannot be added to the invoice yet.";
export const isFeedNotBoundError = (error) => Number(error?.response?.status) === 409 && errorCode(error) === FEED_NOT_BOUND;
// Core's own words, verbatim, and the TX id from details.tx_id when the message does not already carry it.
export const feedNotBoundMessage = (error) => {
  if (!isFeedNotBoundError(error)) return '';
  const body = error.response.data || {};
  const message = typeof body.message === 'string' && body.message.trim() ? body.message : FEED_NOT_BOUND_FALLBACK;
  // The TXN wins: a legacy TX- that Core printed for the same charge is replaced by it (never both on screen).
  const txn = wellFormedTxnId(body.details?.tx_id);
  const legacy = txIdFromHandOffId(body.details?.hand_off_id);
  if (txn && legacy && message.toLowerCase().includes(legacy)) return message.replace(new RegExp(legacy, 'i'), txn);
  const txId = wellFormedTxId(body.details?.tx_id);
  return txId && !message.toLowerCase().includes(txId.toLowerCase()) ? `${message} (${txId})` : message;
};

export const releaseRefusalReason = (error) => {
  const reason = error?.response?.data?.reason;
  return typeof reason === 'string' ? reason : '';
};

// The contract requires `reason` on every draft.line.edit action. The owner-approved mock asks for a typed reason on "Edit". The
// other actions send a plain statement of exactly what was done and where, EXCEPT reject and restore (M004-F0736, ML001-F0235):
// those reasons are the owner's own words and are never filled in here. The adapter refuses to send either without one.
export const OWNER_REASON_ACTIONS = Object.freeze(['reject', 'restore']);
export const MAX_REASON_LENGTH = 500;
export const ACTION_REASONS = Object.freeze({
  add_from_advice: 'Added to the next invoice from the customer ledger page',
  remove: 'Moved back to advice from the customer ledger page',
  add_manual: 'Manual line added on the customer ledger page',
});

export const toMinor = (pounds) => Math.round(Number(pounds) * 100);
const fromMinor = (minor) => {
  const number = Number(minor);
  return Number.isFinite(number) ? number / 100 : 0;
};

// M004-F0799 / Core M001-D034 (relay-only): a SELECTED draft read goes through the CSLPT relay, never as an invoice_id query member
// (Core's typed GET draft routes drop it silently). The relay answers { success, data: { payload } }; the payload is returned in the
// typed shape ({ data: payload }) so normaliseDraft and normalisePreview read it unchanged. target_user_id is the customer whose draft it is.
const relayPath = (orgId) => `/v1/orgs/${encodeURIComponent(orgId)}/modules/ledger/messages`;
const relayRead = async (http, orgId, userId, type, invoiceId) => {
  const response = await http.post(relayPath(orgId), {
    relay_version: 1, type, payload: { invoice_id: invoiceId }, target_user_id: Number(userId),
  });
  return { data: response?.data?.data?.payload ?? null };
};

export function buildLedgerQueuesApi(http) {
  return {
    listAdvice: (orgId, userId) => http.get(`${base(orgId, userId)}/advice`),
    // invoiceId: the M004-F0799 selector, sent only with more than one open draft. With one draft: the typed GET, unchanged.
    getDraft: (orgId, userId, { invoiceId } = {}) => (invoiceId
      ? relayRead(http, orgId, userId, 'draft.get', invoiceId)
      : http.get(`${base(orgId, userId)}/draft`)),
    // M004-F0723: renders the open draft as a watermarked PDF. Zero side effects server-side.
    previewDraft: (orgId, userId, { invoiceId } = {}) => (invoiceId
      ? relayRead(http, orgId, userId, 'draft.preview', invoiceId)
      : http.get(`${base(orgId, userId)}/draft/preview`)),
    // action: 'add_from_advice' | 'add_manual' | 'amount' | 'remove' | 'reject' | 'restore'
    editDraft: (orgId, userId, { action, lineId, adviceId, amount, description, reason, invoiceId }, idempotencyKey) => {
      if (OWNER_REASON_ACTIONS.includes(action)) {
        const typed = typeof reason === 'string' ? reason.trim() : '';
        if (!typed || typed.length > MAX_REASON_LENGTH) {
          return Promise.reject(Object.assign(new Error(`A reason (1-${MAX_REASON_LENGTH} characters) is required to ${action} this item.`), { code: 'REASON_REQUIRED' }));
        }
      }
      const body = { action, reason: OWNER_REASON_ACTIONS.includes(action) ? reason.trim() : (reason || ACTION_REASONS[action]) };
      if (lineId != null) body.line_id = lineId;
      if (adviceId != null) body.advice_id = adviceId;
      if (amount != null) body.amount_minor = toMinor(amount);
      if (description != null) body.description = description;
      if (invoiceId != null) body.invoice_id = invoiceId;
      return http.post(`${base(orgId, userId)}/draft/lines`, body, { headers: { 'Idempotency-Key': idempotencyKey } });
    },
    approveDraft: (orgId, userId, { invoiceId, expectedTotal }, idempotencyKey) => http.post(
      `${base(orgId, userId)}/draft/approve`,
      { invoice_id: invoiceId, expected_total_minor: toMinor(expectedTotal) },
      { headers: { 'Idempotency-Key': idempotencyKey } },
    ),
    // advice.release (Spec 1.2.0): SAL 4, Idempotency-Key required.
    releaseAdvice: (orgId, userId, { handOffId, reason }, idempotencyKey) => http.post(
      `${base(orgId, userId)}/advice/release`,
      { hand_off_id: handOffId, reason },
      { headers: { 'Idempotency-Key': idempotencyKey } },
    ),
    // invoice.line.history (Spec 1.2.0): a read; 404 means the item has no line.
    // M004-F0711: Core's billing read, view=prob (Core ML001-D014 5d.4.1; billing.view + ledger_dispatch_approve). NOT the account
    // gateway: the page reads it only to show a customer's approved-but-failed items under Problems.
    listProblems: (orgId) => http.get(`/v1/orgs/${encodeURIComponent(orgId)}/billing/items`, { params: { view: 'prob' } }),
    getInvoiceLineHistory: (orgId, userId, handOffId) => http.get(`${base(orgId, userId)}/invoice-lines/${encodeURIComponent(handOffId)}/history`),
  };
}

const unwrap = (response) => {
  const body = response?.data;
  if (body && typeof body === 'object' && !Array.isArray(body) && 'data' in body) return body.data;
  return body ?? null;
};
export const errorCode = (error) => {
  const body = error?.response?.data || {};
  if (typeof body.error === 'string') return body.error;
  return body.error?.code || body.code || '';
};

export const isUnsupportedError = (error) => UNSUPPORTED_CODES.includes(errorCode(error));
export const isForbiddenError = (error) => Number(error?.response?.status) === 403;
// M004-F0708 / T-M004-0194 (WebUI #142): Core refuses the queue reads for an actor without ledger manage rights
// and for the actor's own account. Distinct from the 404 capability gate (isRouteNotFoundError): 404 is "not available", this is "not allowed here".
export const isPermissionDeniedError = (error) => isForbiddenError(error) && errorCode(error) === 'LEDGER_ACCOUNT_PERMISSION_DENIED';

// AdviceItem.state (contract enum), §2.44.4. Advice lists only costs NOT on
// the draft (owner ruling 2026-09-30 23:20Z, "Add automatically"):
//   coming         "Coming"         pre-settlement notice             -> Reject
//   being_checked  "Being checked"  settled, automatic landing refused -> Reject
//   deferred       "Deferred"       removed from the draft             -> Add to invoice, Reject
//   rejected       "Rejected"                                          -> Restore (-> Deferred)
// Spec 1.2.0 (ML001-F0122): a Core-notice item with hand_off_id, notice_state
// 'awaiting_approval' and session_ended true ALSO offers "Add to invoice", which is
// advice.release (Core posts its own held posting), not the draft add above.
// An `entry` (an uninvoiced charge, already in the book) is never rejectable
// (§2.44.4, 409 LEDGER_DRAFT_LINE_BOOKED), so it gets no Reject. A state
// outside the contract enum is not shown.
const STATE_ACTIONS = {
  coming: { add: false, reject: true, restore: false },
  being_checked: { add: false, reject: true, restore: false },
  deferred: { add: true, reject: true, restore: false },
  rejected: { add: false, reject: false, restore: true },
};

export function normaliseAdvice(response) {
  const body = unwrap(response);
  const rows = Array.isArray(body?.items) ? body.items : [];
  return rows.map((row) => {
    const pill = STATE_ACTIONS[row?.state] ? row.state : null;
    if (!pill || !row?.advice_id) return null;
    const allowed = STATE_ACTIONS[pill];
    const isCharge = row.source === 'entry';
    // ML001-F0122, ML001-F0123 / ACCT-RULE-004: the three notice members arrive together and
    // only on a Core-notice item. Release is offered ONLY when all three say
    // so: a hand_off_id, notice_state exactly 'awaiting_approval' and
    // session_ended exactly true (a missing or merely truthy value is not enough).
    const handOffId = typeof row.hand_off_id === 'string' && row.hand_off_id ? row.hand_off_id : '';
    // M004-F0727 (owner ruling 2026-10-07): the Ledger reads `coming` ONLY for a future service start; a started or ended notice
    // is `being_checked`. So the release offer must not depend on `coming` (an ended session never is): it follows the members,
    // and the "Reason (required)" prompt follows the STATE the server derived from the start (promptReason).
    const canRelease = (pill === 'coming' || pill === 'being_checked') && Boolean(handOffId) && row.notice_state === 'awaiting_approval' && row.session_ended === true;
    return {
      adviceId: String(row.advice_id),
      source: row.source || '',
      pill,
      date: row.date || '',
      description: row.description || '',
      note: row.note || '',
      amount: fromMinor(row.amount_minor),
      currency: row.currency || body?.currency || 'GBP',
      handOffId,
      // M004-F0715/F0716 (Spec 1.4.0): the Ledger's own stage for the item (ACCT-TX-013), '' when an older Ledger sends none.
      stage: typeof row.stage === 'string' ? row.stage : '',
      noticeState: row.notice_state || '',
      sessionEnded: row.session_ended === true,
      canRelease,
      // M004-F0727: ask for a reason only for an item still held in Core (it carries the notice members) whose start is ahead (state `coming`).
      promptReason: canRelease && pill === 'coming',
      // An unsettled notice that is no longer Coming is ready / awaiting approval, not 'being checked'.
      awaitingApproval: pill === 'being_checked' && Boolean(row.notice_state) && !isCharge,
      // A held item is released through advice.release; offering the draft add as well would be two buttons for one action.
      canAdd: allowed.add && !canRelease,
      canReject: allowed.reject && !isCharge,
      canRestore: allowed.restore,
    };
  }).filter(Boolean);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Formats a date as "29 Sep 2026". The result does not depend on the runtime's
// ICU locale data, and the date is read in UTC so a date-only value never
// shifts by a day.
export function formatDay(raw) {
  if (!raw) return '';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return String(raw);
  return `${String(date.getUTCDate()).padStart(2, '0')} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

// §2.44.12.3 audit rows. The contract's DraftEdit carries amount edits only
// ({from_minor, to_minor, reason, actor_display, at}):
// "Edited £a → £b · reason: <reason> · by <actor> · <date>".
const auditNote = (edit, money, currency) => {
  if (edit?.from_minor == null || edit?.to_minor == null) return null;
  const by = edit.actor_display ? ` · by ${edit.actor_display}` : '';
  const when = edit.at ? ` · ${formatDay(edit.at)}` : '';
  return `Edited ${money(fromMinor(edit.from_minor), currency)} → ${money(fromMinor(edit.to_minor), currency)} · reason: ${edit.reason || ''}${by}${when}`;
};

// draft.get: `draft` is null when the customer has no open draft.
export function normaliseDraft(response, money) {
  const body = unwrap(response);
  const draft = body && typeof body === 'object' ? body.draft : null;
  if (!draft || typeof draft !== 'object' || !draft.invoice_id) return null;
  const currency = draft.currency || 'GBP';
  const lines = (Array.isArray(draft.lines) ? draft.lines : []).map((line) => ({
    lineId: String(line?.line_id || ''),
    source: line?.source || '',
    // Spec 1.4.0 (ACCT-TX-013): EVERY DraftLine carries hand_off_id (a manual line gets a Ledger-minted TX); an older Ledger sends none, and then no chip shows.
    handOffId: typeof line?.hand_off_id === 'string' ? line.hand_off_id : '',
    stage: typeof line?.stage === 'string' ? line.stage : '',
    description: line?.description || '',
    amount: fromMinor(line?.amount_minor),
    // M004-F0799: the share of an apportioned booking (null when the line is not a part); the Ledger's number, never recomputed.
    share: line?.share_of || null,
    audit: [
      // The contract has no manual-line audit row (no actor/date on the wire); the line's own source says it.
      ...(line?.source === 'manual' ? ['Manual line'] : []),
      ...(Array.isArray(line?.edits) ? line.edits : []).map((edit) => auditNote(edit, money, currency)).filter(Boolean),
    ],
  })).filter((line) => line.lineId);
  return {
    invoiceId: String(draft.invoice_id),
    currency,
    lines,
    net: fromMinor(draft.net_minor),
    vat: fromMinor(draft.vat_minor),
    vatRegistered: draft.vat_registered ?? null,
    total: fromMinor(draft.total_minor),
    // M004-F0799: the group's label and the bill-to DISPLAY NAME only (never an email or an address).
    groupLabel: typeof draft.group_label === 'string' ? draft.group_label : '',
    billTo: typeof draft.bill_to?.display_name === 'string' ? draft.bill_to.display_name : '',
  };
}

// M004-F0711: Core's billing items, view=prob, narrowed to ONE customer's claimed postings (a hand_off_id: dispatch_failed, or a
// notice the Ledger could not be told). Rows with no hand_off_id are pre-claim previews (no price, no teacher): not on a
// customer's ledger, they stay on the Billing tab. Core's own words (status_text, problem.text) are carried, never re-derived.
export function normaliseProblems(response, userId) {
  const body = unwrap(response);
  const groups = Array.isArray(body?.groups) ? body.groups : [];
  const rows = [];
  groups.forEach((group) => (Array.isArray(group?.items) ? group.items : []).forEach((row) => {
    if (!row || row.status !== 'prob') return;
    if (row.user_id == null || String(row.user_id) !== String(userId)) return;
    if (typeof row.hand_off_id !== 'string' || !row.hand_off_id) return;
    rows.push({
      itemId: String(row.id),
      handOffId: row.hand_off_id,
      description: [row.booking_title, row.room].filter((part) => typeof part === 'string' && part).join(' · '),
      date: typeof row.date === 'string' ? row.date : '',
      amount: fromMinor(row.amount_minor),
      currency: row.currency || 'GBP',
      statusText: typeof row.status_text === 'string' ? row.status_text : '',
      problemText: typeof row.problem?.text === 'string' ? row.problem.text : '',
    });
  }));
  return rows;
}

// draft.approve (approve = issue, §2.44.8).
export function normaliseIssued(response) {
  const body = unwrap(response) || {};
  return {
    invoiceNumber: body.invoice_number || '',
    total: fromMinor(body.total_minor),
    currency: body.currency || 'GBP',
    emailed: typeof body.sent === 'boolean' ? body.sent : null,
    sendError: body.send_error || '',
  };
}

// advice.release 200 (Spec 1.4.0 ReleaseData, C12): Core ACCEPTED the release and tells the truth about where the cost is.
// `conditions[]` (ACCT-TX-005) are never refusals; their `message` and `next_action` are Core's sentences, carried verbatim.
export function normaliseRelease(response) {
  const body = unwrap(response) || {};
  const conditions = (Array.isArray(body.conditions) ? body.conditions : [])
    .filter((condition) => condition && typeof condition.message === 'string' && condition.message.trim())
    .map((condition) => ({
      code: typeof condition.code === 'string' ? condition.code : '',
      message: condition.message,
      nextAction: typeof condition.next_action === 'string' ? condition.next_action : '',
    }));
  return {
    stage: typeof body.stage === 'string' ? body.stage : '',
    nextAction: typeof body.next_action === 'string' && body.next_action.trim() ? body.next_action : '',
    conditions,
    replayed: body.replayed === true,
  };
}

// invoice.line.history (Spec 1.2.0, ACCT-RULE-006). Amounts are minor units and
// the payload names no currency: the Ledger bills in the organisation's currency,
// passed in by the caller (GBP when the caller knows none).
const valuesView = (values) => {
  if (!values || typeof values !== 'object') return null;
  const view = {};
  if (Number.isFinite(Number(values.amount_minor)) && values.amount_minor != null) view.amount = fromMinor(values.amount_minor);
  if (typeof values.description === 'string' && values.description) view.description = values.description;
  return Object.keys(view).length ? view : null;
};

const actorName = (edit) => (typeof edit?.actor_display_name === 'string' && edit.actor_display_name.trim() ? edit.actor_display_name : '');

export function normaliseLineHistory(response) {
  const body = unwrap(response);
  if (!body || typeof body !== 'object' || !body.hand_off_id) return null;
  const invoice = body.invoice && typeof body.invoice === 'object' ? {
    invoiceId: String(body.invoice.invoice_id || ''),
    number: body.invoice.number || '',
    status: body.invoice.status || '',
  } : null;
  const edits = (Array.isArray(body.edits) ? body.edits : []).map((edit) => ({
    at: edit?.at || '',
    invoiceId: edit?.invoice_id ?? null,
    // actor_kind 'system' (a sweep or landing) carries no person: shown as "System".
    byUser: edit?.actor_kind === 'user',
    // M004-F0700 (Spec 1.3.0): Core's resolved actor_display_name wins for a user edit when it is a
    // non-blank string. null/absent/blank (deleted or unknown user, or none recorded) keeps the
    // fallback exactly as before; a name is never invented. A system edit is always "System".
    actor: edit?.actor_kind === 'user' ? (actorName(edit) || edit?.actor_display || 'A user') : 'System',
    action: edit?.action || '',
    before: valuesView(edit?.before),
    after: valuesView(edit?.after),
    reason: edit?.reason || '',
  }));
  return {
    handOffId: String(body.hand_off_id),
    txId: displayTxId({ txId: body.tx_id, handOffId: body.hand_off_id }),
    coreAmount: fromMinor(body.core_amount_minor),
    currentAmount: fromMinor(body.current_amount_minor),
    invoice,
    edits,
  };
}

// M004-F0723 (previewLedgerDraft 200 `data`): the contract caps the decoded PDF at 5 MiB.
export const PREVIEW_MAX_BYTES = 5 * 1024 * 1024;
export const PREVIEW_DRAFT_NOT_FOUND = 'LEDGER_DRAFT_NOT_FOUND';
const SEVERITY_ORDER = { blocker: 0, warning: 1, info: 2 };
// Returns null when the body is not a usable preview (no PDF): the caller shows a plain failure sentence.
export function normalisePreview(response) {
  const body = unwrap(response);
  if (!body || typeof body !== 'object' || typeof body.pdf_base64 !== 'string' || !body.pdf_base64) return null;
  const items = (Array.isArray(body.readiness?.items) ? body.readiness.items : [])
    .filter((item) => item && typeof item.message === 'string' && item.message)
    .map((item) => ({ code: String(item.code || ''), severity: SEVERITY_ORDER[item.severity] != null ? item.severity : 'info', message: item.message }))
    .sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
  return {
    pdfBase64: body.pdf_base64,
    filename: typeof body.filename === 'string' && body.filename ? body.filename : 'DRAFT-preview.pdf',
    nextInvoiceNumber: typeof body.next_invoice_number === 'string' ? body.next_invoice_number : '',
    ready: body.readiness?.ready === true,
    items,
  };
}
export const isDraftNotFoundError = (error) => errorCode(error) === PREVIEW_DRAFT_NOT_FOUND;
